import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Typography, List, Tooltip, Form, Divider, InputNumber } from "antd";
import { SettingOutlined, DeleteOutlined, MenuOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { IconChevronUp, IconChevronDown, IconX } from "@tabler/icons-react";
const { Title, Text } = Typography;

// Type definitions
type ValueType = {
    limit: number;
    orders: {
        label: string;
        value: string;
        direction: "asc" | "desc";
        meta?: Record<string, any>;
    }[];
};

type OptionType = {
    label: string;
    value: string;
    meta?: Record<string, any>;
};

type SearchQueryOrderLimitTemplateV2Props = {
    initValue: ValueType;
    orderOptions: OptionType[];
    onCancel?: () => void;
    onApply: (value: ValueType) => void;
    afterOpenChange?: (open: boolean) => void;
    validate?: {
        fields: string[];
    };
};

// Sortable item component for sort order list
const SortableOrderItem: React.FC<{
    id: string;
    item: ValueType["orders"][0];
    onToggleDirection: (name: string) => void;
    onRemove: (name: string) => void;
}> = ({ id, item, onToggleDirection, onRemove }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <List.Item ref={setNodeRef} style={style} {...attributes} className="rounded-md mb-2 p-2  bg-white  ">
            <div className="flex items-center justify-between w-full">
                <div className="flex items-center space-x-3">
                    <div {...listeners} className="cursor-grab">
                        <MenuOutlined className="text-gray-400" />
                    </div>
                    <Text strong>{item.label}</Text>
                </div>
                <div className="flex items-center space-x-2">
                    <Button size="small" onClick={() => onToggleDirection(item.value)}>
                        {item.direction === "asc" ? (
                            <IconChevronUp className="text-green-500" size={16} />
                        ) : (
                            <IconChevronDown className="text-red-500" size={16} />
                        )}
                        {item.direction.toUpperCase()}
                    </Button>
                    <Button
                        size="small"
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => onRemove(item.value)}
                    />
                </div>
            </div>
        </List.Item>
    );
};

// Available columns item component
const AvailableColumnItem: React.FC<{
    option: OptionType;
    onAdd: (option: OptionType) => void;
    isSelected: boolean;
}> = ({ option, onAdd, isSelected }) => {
    return (
        <List.Item
            className={`border-none rounded-md mb-2 p-2  transition-colors duration-100 ${
                isSelected ? "bg-blue-50 border-blue-300" : "bg-gray-100 hover:bg-gray-200 "
            }`}
        >
            <div className="flex items-center justify-between w-full">
                <Text className={isSelected ? "text-gray-400" : ""}>{option.label}</Text>

                {!isSelected && <Button onClick={() => onAdd(option)} type="text" icon={<ArrowRightOutlined />} />}
            </div>
        </List.Item>
    );
};

const INPUT_LIMIT_ID = "limit-config-v2";
export const SearchQueryOrderLimitTemplateV2: React.FC<SearchQueryOrderLimitTemplateV2Props> = ({
    initValue,
    orderOptions,
    onCancel,
    onApply,
    validate,
    afterOpenChange,
}) => {
    const [form] = Form.useForm();
    const [open, setOpen] = useState(false);
    const [currentValue, setCurrentValue] = useState<ValueType>(initValue);
    const [isSetInitValue, setSetInitValue] = useState(false);

    const sensors = useSensors(useSensor(PointerSensor));

    // Notify parent about open state changes
    useEffect(() => {
        afterOpenChange?.(open);
    }, [open, afterOpenChange]);

    // Get available options (not already in sort order)
    const availableOptions = useMemo(() => {
        const selectedNames = currentValue.orders.map((order) => order.value);
        return orderOptions.filter((option) => !selectedNames.includes(option.value));
    }, [orderOptions, currentValue.orders]);

    // Handle drag end for reordering
    const handleDragEnd = (event: any) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
            const oldIndex = currentValue.orders.findIndex((item) => item.value === active.id);
            const newIndex = currentValue.orders.findIndex((item) => item.value === over?.id);
            const newOrders = arrayMove(currentValue.orders, oldIndex, newIndex);

            setCurrentValue((prev) => ({
                ...prev,
                orders: newOrders,
            }));
        }
    };

    // Add column to sort order
    const handleAddColumn = (option: OptionType) => {
        const newOrder: ValueType["orders"][0] = {
            label: option.label,
            value: option.value,
            direction: "asc" as const,
            meta: option.meta,
        };

        setCurrentValue((prev) => ({
            ...prev,
            orders: [...prev.orders, newOrder],
        }));
    };

    // Remove column from sort order
    const handleRemoveColumn = (name: string) => {
        setCurrentValue((prev) => ({
            ...prev,
            orders: prev.orders.filter((order) => order.value !== name),
        }));
    };

    // Toggle sort direction
    const handleToggleDirection = (name: string) => {
        setCurrentValue((prev) => ({
            ...prev,
            orders: prev.orders.map((order) =>
                order.value === name ? { ...order, direction: order.direction === "asc" ? "desc" : "asc" } : order,
            ),
        }));
    };

    // Handle limit change
    const handleLimitChange = (limit: number) => {
        setCurrentValue((prev) => ({
            ...prev,
            limit,
        }));
    };

    // Handle modal close
    const handleCancel = () => {
        setOpen(false);
        onCancel?.();
    };

    // Handle apply changes
    const handleApply = async () => {
        if (validate) {
            form.validateFields(validate.fields)
                .then(() => {
                    onApply(currentValue);
                    setOpen(false);
                })
                .catch((error) => {
                    document.getElementById(INPUT_LIMIT_ID)?.scrollIntoView({ behavior: "smooth" });
                });
        } else {
            onApply(currentValue);
            setOpen(false);
        }
    };

    const isValueChanged = useMemo(() => {
        return (
            currentValue.limit !== initValue.limit ||
            JSON.stringify(currentValue.orders) !== JSON.stringify(initValue.orders)
        );
    }, [currentValue, JSON.stringify(initValue)]);

    return (
        <>
            <Tooltip title="Configure result listing">
                <Button icon={<SettingOutlined />} onClick={() => setOpen(true)} />
            </Tooltip>
            <Form form={form}>
                <Modal
                    title={
                        <div className="flex flex-col ">
                            <p className="text-[24px] font-bold m-0">Configure Result Listing</p>
                            <span className="font-normal text-sm text-gray-500">
                                Drag to reorder sort priority. Set a record limit for your query.
                            </span>
                            <Divider className="my-2" />
                        </div>
                    }
                    open={open}
                    width={800}
                    centered
                    destroyOnHidden
                    afterOpenChange={(open) => {
                        if (open) {
                            form.setFieldsValue({
                                limit: currentValue.limit,
                            });
                            setCurrentValue(initValue);
                            setSetInitValue(true);
                            return;
                        }
                        setSetInitValue(false);
                    }}
                    closable={{
                        closeIcon: <IconX size={24} onClick={handleCancel} />,
                    }}
                    footer={
                        <div className="flex justify-end space-x-2">
                            <Button onClick={handleCancel}>Cancel</Button>
                            <Button type="primary" onClick={handleApply} disabled={!isValueChanged}>
                                Apply Changes
                            </Button>
                        </div>
                    }
                    classNames={{
                        content: "p-0 bg-white",
                        footer: "bg-gray-100 px-[20px] py-[18px] rounded-b-md",
                        header: "px-[20px] py-[18px] bg-white",
                        body: " px-[20px]",
                    }}
                >
                    <div className="h-[60vh] overflow-y-auto small-scrollbar">
                        {/* Record Limit Section */}
                        <div className="mb-6" id={INPUT_LIMIT_ID}>
                            <Title level={5} className="mb-3">
                                Record Limit
                            </Title>
                            <Form.Item
                                name="limit"
                                label=""
                                rules={[
                                    {
                                        required: true,
                                        validator: (_, value) => {
                                            if (value < 1 || value > 10000) {
                                                return Promise.reject(new Error("Limit must be between 1 and 10000"));
                                            }
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <InputNumber
                                    min={1}
                                    max={10000}
                                    precision={0}
                                    value={currentValue.limit}
                                    onChange={(value) => handleLimitChange(value ?? 100)}
                                    style={{ width: "200px" }}
                                />
                            </Form.Item>
                        </div>

                        {/* Two Column Layout */}
                        <div className="flex gap-6 min-h-[75%]">
                            {/* Available Columns */}
                            <OrderContainer className="flex-[3] flex flex-col" title="Available Columns">
                                <List
                                    dataSource={availableOptions}
                                    locale={{ emptyText: "All columns are already selected" }}
                                    renderItem={(option) => (
                                        <AvailableColumnItem
                                            key={option.value}
                                            option={option}
                                            onAdd={handleAddColumn}
                                            isSelected={false}
                                        />
                                    )}
                                />
                            </OrderContainer>

                            {/* Sort Order */}
                            <OrderContainer className="flex-[4] flex flex-col" title="Sort Order (Drag to reorder)">
                                {currentValue.orders.length === 0 ? (
                                    <div className="text-center text-gray-400 py-8">
                                        <Text>No sort order configured</Text>
                                        <br />
                                        <Text type="secondary" className="text-sm">
                                            Click the arrow on an available column to add it here.
                                        </Text>
                                    </div>
                                ) : (
                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragEnd={handleDragEnd}
                                        modifiers={[restrictToVerticalAxis]}
                                    >
                                        <SortableContext
                                            items={currentValue.orders.map((item) => item.value)}
                                            strategy={verticalListSortingStrategy}
                                        >
                                            {currentValue.orders.map((item) => (
                                                <SortableOrderItem
                                                    key={item.value}
                                                    id={item.value}
                                                    item={item}
                                                    onToggleDirection={handleToggleDirection}
                                                    onRemove={handleRemoveColumn}
                                                />
                                            ))}
                                        </SortableContext>
                                    </DndContext>
                                )}
                            </OrderContainer>
                        </div>
                    </div>
                </Modal>
            </Form>
        </>
    );
};

const OrderContainer = ({
    children,
    className,
    title,
}: {
    children: React.ReactNode;
    className?: string;
    title: string;
}) => {
    return (
        <div className={` ${className}`}>
            <div className="flex-[3] flex flex-col">
                <Title level={5} className="mb-3">
                    {title}
                </Title>
                <div className={"flex-1 border-[1px] border-gray-200  border-solid bg-gray-100 rounded-md p-2 "}>
                    {children}
                </div>
            </div>
        </div>
    );
};
